import { enqueueSnackbar } from "notistack"
import { api } from "../../../api"
import { ChecklistItem, ExpenseCategory } from "../types/checklist"

const module_url = "/complaint"
const module_url_2 = "/accounts"
const TAB_TO_CATEGORY_LABEL = { all: "all", outright: "Outright", cpo: "CPO" }

// Pull a human-readable message out of an RTK Query error. The backend variously returns
// { Error } / { error } / { message }; fall back to a JS error message, then a generic string.
const getErrorMsg = (error: any) =>
    error?.error?.data?.Error ||
    error?.error?.data?.error ||
    error?.error?.data?.message ||
    error?.message ||
    "Something went wrong!"

const complaintsService = api.injectEndpoints({
    endpoints: build => ({
        getAllComplaints: build.query<any, { data: any; selectedValue: number; page: number }>({
            query: ({ data, selectedValue, page }) => ({
                url: `${module_url}/all_complaints`,
                useV3: true,
                params: {
                    category: data?.category
                        ? TAB_TO_CATEGORY_LABEL[data?.category as keyof typeof TAB_TO_CATEGORY_LABEL]
                        : "all",
                    complaint_subtype_id: data.complaints_subtype_id?.length
                        ? JSON.stringify(data.complaints_subtype_id)
                        : "",
                    complaint_type_id: data.complaint_type_id?.length ? JSON.stringify(data.complaint_type_id) : "",
                    zone: data.zone?.length ? JSON.stringify(data.zone) : "",
                    country_id: data.country?.length ? JSON.stringify(data.country) : "",
                    status: data.status?.length ? JSON.stringify(data.status) : "",
                    state_id: data.state_id?.length ? JSON.stringify(data.state_id) : "",
                    city_id: data.city_id?.length ? JSON.stringify(data.city_id) : "",
                    oem_id: data.oem_id?.length ? JSON.stringify(data.oem_id) : "",
                    account_id: data.account_id?.length ? JSON.stringify(data.account_id) : "",
                    search_key: data.search_key ?? "",
                    raised_by_email: data.raised_by_email ?? "",
                    priority: data.priority?.length ? JSON.stringify(data.priority) : "",
                    statiq_engineer_ids: data.statiq_engineer_ids?.length
                        ? JSON.stringify(data.statiq_engineer_ids)
                        : "",
                    asp_company_names: data.assigned_asp_name?.length ? JSON.stringify(data.assigned_asp_name) : "",
                    asp_engineer_ids: data.asp_engineer_ids?.length ? JSON.stringify(data.asp_engineer_ids) : "",
                    service_complaint_type_id: data.service_complaint_type_id?.length
                        ? JSON.stringify(data.service_complaint_type_id)
                        : "",
                    service_complaint_subtype_id: data.service_complaint_subtype_id?.length
                        ? JSON.stringify(data.service_complaint_subtype_id)
                        : "",
                    updated_complaint_type_id: data.update_complaint_type?.length
                        ? JSON.stringify(data.update_complaint_type)
                        : "",
                    updated_complaint_subtype_id: data.update_complaint_subtype?.length
                        ? JSON.stringify(data.update_complaint_subtype)
                        : "",
                    parent_account_id: data.parent_account_id?.length ? JSON.stringify(data.parent_account_id) : "",
                    resolution_remark_id: data.resolution_remark_id?.length
                        ? JSON.stringify(data.resolution_remark_id?.[0])
                        : "",
                    charger_type_filter: data.charger_type?.length ? data.charger_type?.[0] : "",
                    sku_filter: data.charger_sku?.length ? data.charger_sku?.[0] : "",
                    department: data.department?.length ? JSON.stringify(data.department) : "",
                    part_replacement: data.part_replacement ?? "",
                    site_visit: data.site_visit ?? "",
                    on_hold: data.on_hold?.length ? data.on_hold?.[0] : "",
                    acceptance_status: data.acceptance_status?.length ? data.acceptance_status.join(",") : "",
                    all_complaints: data.all_complaints ?? 1,
                    created_at_start: data.created_at_start ?? "",
                    created_at_end: data?.created_at_end ?? "",
                    ...(data.is_executive && { is_executive: data.is_executive }),
                    ...(data.engineer_id && { engineer_id: data.engineer_id }),
                    source_system: data?.source_system ?? "",
                    doa: data.doa?.length ? data.doa.join(",") : "",
                    age: data?.age ?? "[]",
                    age_op: data?.age_op ?? "",
                    per_page: selectedValue,
                    page: page,
                },
            }),
            providesTags: ["Complaints", "ComplaintsAction"],
        }),
        getComplaintDetail: build.query({
            query: (complaint_id: string) => ({
                url: `${module_url}/complaint_details`,
                useV3: true,
                method: "GET",
                params: {
                    complaint_id: complaint_id ?? "",
                },
            }),
            providesTags: ["ComplaintsAction"],
        }),
        getComplaintsButtonDetail: build.query({
            query: () => ({
                url: `${module_url}/status`,
                useV3: true,
                method: "GET",
            }),
            providesTags: ["Complaints"],
        }),
        getResolvedResolutionRemarks: build.query<any, void>({
            query: () => ({
                url: `${module_url}/resolution_remarks`,
                useV3: true,
            }),
            transformResponse: (response: any) => {
                return response?.map((item: any) => ({
                    id: item.id,
                    title: item.name,
                }))
            },
        }),
        getParentAccounts: build.query<any, void>({
            query: () => ({
                url: `${module_url_2}/get_parent_accounts`,
            }),
            transformResponse: (response: any) => {
                return response?.data?.map((item: any) => ({
                    id: item.id,
                    title: item.name,
                }))
            },
        }),
        getEngineersData: build.query<any, { department?: string } | void>({
            query: params => ({
                url: `${module_url}/engineers`,
                useV3: true,
                params: {
                    company_type: "Statiq",
                    company_id: 1,
                    ...(params?.department ? { department: params.department } : {}),
                },
            }),
            transformResponse: (response: any) => {
                return response?.data?.map((item: any) => ({
                    id: item.id,
                    title: item.name,
                }))
            },
        }),
        getASPData: build.query<any, void>({
            query: () => ({
                url: `${module_url}/companies`,
                useV3: true,
                params: {
                    company_type: "Asp",
                },
            }),
            transformResponse: (response: any) => {
                return response?.map((item: any) => ({
                    id: item.id,
                    title: item.name,
                }))
            },
        }),
        getAspEngineersData: build.query<any, { company_name?: string; company_id?: number }>({
            query: params => ({
                url: `${module_url}/asp_engineers`,
                useV3: true,
                params: params?.company_name
                    ? {
                          company_name: params?.company_name ?? "",
                          company_id: params?.company_id ?? "",
                      }
                    : {},
            }),
            transformResponse: (response: any) => {
                return response?.map((item: any) => ({
                    id: item.id,
                    title: item.name,
                }))
            },
        }),
        getResolutionRemarks: build.query<any, void>({
            query: () => ({
                url: `${module_url}/complaint_resolution_remarks_api`,
                useV3: true,
            }),
            transformResponse: (response: any) => {
                const list = Array.isArray(response) ? response : (response?.data ?? [])
                return list.map((item: any) => ({
                    id: item.id,
                    title: item.name ?? item.title ?? item.label,
                    code: item.code ?? item.key ?? null,
                }))
            },
        }),
        getComplaintsType: build.query<any, { is_admin?: number }>({
            query: params => ({
                url: `${module_url}/complaint_types_api`,
                useV3: true,
                params: params?.is_admin
                    ? {
                          is_admin: params.is_admin,
                      }
                    : {},
            }),
            transformResponse: (response: any) => {
                return response.map((item: any) => ({
                    id: item?.id,
                    title: item?.label ?? item?.name ?? item?.title,
                }))
            },
        }),
        getComplaintsSubType: build.query<any, { is_admin?: number; type_code?: string }>({
            query: params => ({
                url: `${module_url}/complaint_subtypes_api`,
                useV3: true,
                params: {
                    ...(params?.is_admin ? { is_admin: params.is_admin } : {}),
                    ...(params?.type_code ? { type_code: params.type_code } : {}),
                },
            }),
            transformResponse: (response: any) => {
                return response.map((item: any) => ({
                    id: item?.id,
                    title: item?.label ?? item?.name ?? item?.title,
                    // Part 2 — department auto-selection: subtype carries its default + allowed set.
                    default_department: item?.default_department ?? null,
                    allowed_departments: item?.allowed_departments ?? [],
                }))
            },
        }),
        getEngineerCompanies: build.query<any, void>({
            query: () => ({
                url: `${module_url}/companies`,
                useV3: true,
            }),
            transformResponse: (response: any) => {
                return response?.map((item: any) => ({
                    id: item.id,
                    title: item.name,
                }))
            },
        }),
        getAccounts: build.query<any, void>({
            query: () => ({
                url: `${module_url}/get_parent_Accounts`,
                useV3: true,
            }),
            transformResponse: (response: any) => {
                return response?.map((item: any) => ({
                    id: item.id,
                    title: item.account_name,
                }))
            },
        }),

        getPartReplacement: build.query<any, { complaint_id: number }>({
            query: ({ complaint_id }) => ({
                url: `${module_url}/part_replacement`,
                useV3: true,
                params: {
                    complaint_id: complaint_id,
                },
            }),
            providesTags: ["PartReplacement"],
        }),

        getComplaintMetrics: build.query<any, any>({
            query: ({ data, selectedValue, page }: any) => ({
                url: `${module_url}/complaint_metrics`,
                useV3: true,
                params: {
                    category: "",
                    complaint_subtype_id: data.complaints_subtype_id?.length
                        ? JSON.stringify(data.complaints_subtype_id)
                        : "",
                    complaint_type_id: data.complaint_type_id?.length ? JSON.stringify(data.complaint_type_id) : "",
                    zone: data.zone?.length ? JSON.stringify(data.zone) : "",
                    country_id: data.country?.length ? JSON.stringify(data.country) : "",
                    status: data.status?.length ? JSON.stringify(data.status) : "",
                    state_id: data.state_id?.length ? JSON.stringify(data.state_id) : "",
                    city_id: data.city_id?.length ? JSON.stringify(data.city_id) : "",
                    oem_id: data.oem_id?.length ? JSON.stringify(data.oem_id) : "",
                    account_id: data.account_id?.length ? JSON.stringify(data.account_id) : "",
                    search_key: data.search_key ?? "",
                    raised_by_email: data.raised_by_email ?? "",
                    priority: data.priority?.length ? JSON.stringify(data.priority) : "",
                    statiq_engineer_ids: data.statiq_engineer_ids?.length
                        ? JSON.stringify(data.statiq_engineer_ids)
                        : "",
                    asp_company_names: data.assigned_asp_name?.length ? JSON.stringify(data.assigned_asp_name) : "",
                    asp_engineer_ids: data.asp_engineer_ids?.length ? JSON.stringify(data.asp_engineer_ids) : "",
                    service_complaint_type_id: data.service_complaint_type_id?.length
                        ? JSON.stringify(data.service_complaint_type_id)
                        : "",
                    service_complaint_subtype_id: data.service_complaint_subtype_id?.length
                        ? JSON.stringify(data.service_complaint_subtype_id)
                        : "",
                    updated_complaint_type_id: data.update_complaint_type?.length
                        ? JSON.stringify(data.update_complaint_type)
                        : "",
                    updated_complaint_subtype_id: data.update_complaint_subtype?.length
                        ? JSON.stringify(data.update_complaint_subtype)
                        : "",
                    parent_account_id: data.parent_account_id?.length ? JSON.stringify(data.parent_account_id) : "",
                    resolution_remark_id: data.resolution_remark_id?.length
                        ? JSON.stringify(data.resolution_remark_id?.[0])
                        : "",
                    charger_type_filter: data.charger_type?.length ? data.charger_type?.[0] : "",
                    sku_filter: data.charger_sku?.length ? data.charger_sku?.[0] : "",
                    department: data.department?.length ? JSON.stringify(data.department) : "",
                    part_replacement: data.part_replacement ?? "",
                    site_visit: data.site_visit ?? "",
                    on_hold: data.on_hold?.length ? data.on_hold?.[0] : "",
                    acceptance_status: data.acceptance_status?.length ? data.acceptance_status.join(",") : "",
                    all_complaints: data.all_complaints ?? 1,
                    created_at_start: data.created_at_start ?? "",
                    created_at_end: data?.created_at_end ?? "",
                    ...(data.is_executive && { is_executive: data.is_executive }),
                    ...(data.engineer_id && { engineer_id: data.engineer_id }),
                    source_system: data?.source_system ?? "",
                    doa: data.doa?.length ? data.doa.join(",") : "",
                    age: data?.age ?? "[]",
                    age_op: data?.age_op ?? "",
                },
            }),
            providesTags: ["Complaints"],
        }),

        getComplaintTabsData: build.query<any, any>({
            query: ({ data, selectedValue, page }: any) => ({
                url: `${module_url}/complaint_metrics_kpis`,
                useV3: true,
                params: {
                    category: "",
                    complaint_subtype_id: data.complaints_subtype_id?.length
                        ? JSON.stringify(data.complaints_subtype_id)
                        : "",
                    complaint_type_id: data.complaint_type_id?.length ? JSON.stringify(data.complaint_type_id) : "",
                    zone: data.zone?.length ? JSON.stringify(data.zone) : "",
                    country_id: data.country?.length ? JSON.stringify(data.country) : "",
                    status: data.status?.length ? JSON.stringify(data.status) : "",
                    state_id: data.state_id?.length ? JSON.stringify(data.state_id) : "",
                    city_id: data.city_id?.length ? JSON.stringify(data.city_id) : "",
                    oem_id: data.oem_id?.length ? JSON.stringify(data.oem_id) : "",
                    account_id: data.account_id?.length ? JSON.stringify(data.account_id) : "",
                    search_key: data.search_key ?? "",
                    raised_by_email: data.raised_by_email ?? "",
                    priority: data.priority?.length ? JSON.stringify(data.priority) : "",
                    statiq_engineer_ids: data.statiq_engineer_ids?.length
                        ? JSON.stringify(data.statiq_engineer_ids)
                        : "",
                    asp_company_names: data.assigned_asp_name?.length ? JSON.stringify(data.assigned_asp_name) : "",
                    asp_engineer_ids: data.asp_engineer_ids?.length ? JSON.stringify(data.asp_engineer_ids) : "",
                    service_complaint_type_id: data.service_complaint_type_id?.length
                        ? JSON.stringify(data.service_complaint_type_id)
                        : "",
                    service_complaint_subtype_id: data.service_complaint_subtype_id?.length
                        ? JSON.stringify(data.service_complaint_subtype_id)
                        : "",
                    updated_complaint_type_id: data.update_complaint_type?.length
                        ? JSON.stringify(data.update_complaint_type)
                        : "",
                    updated_complaint_subtype_id: data.update_complaint_subtype?.length
                        ? JSON.stringify(data.update_complaint_subtype)
                        : "",
                    parent_account_id: data.parent_account_id?.length ? JSON.stringify(data.parent_account_id) : "",
                    resolution_remark_id: data.resolution_remark_id?.length
                        ? JSON.stringify(data.resolution_remark_id?.[0])
                        : "",
                    charger_type_filter: data.charger_type?.length ? data.charger_type?.[0] : "",
                    sku_filter: data.charger_sku?.length ? data.charger_sku?.[0] : "",
                    department: data.department?.length ? JSON.stringify(data.department) : "",
                    part_replacement: data.part_replacement ?? "",
                    site_visit: data.site_visit ?? "",
                    on_hold: data.on_hold?.length ? data.on_hold?.[0] : "",
                    acceptance_status: data.acceptance_status?.length ? data.acceptance_status.join(",") : "",
                    all_complaints: data.all_complaints ?? 1,
                    created_at_start: data.created_at_start ?? "",
                    created_at_end: data?.created_at_end ?? "",
                    ...(data.is_executive && { is_executive: data.is_executive }),
                    ...(data.engineer_id && { engineer_id: data.engineer_id }),
                    source_system: data?.source_system ?? "",
                    doa: data.doa?.length ? data.doa.join(",") : "",
                    age: data?.age ?? "[]",
                    age_op: data?.age_op ?? "",
                },
            }),
        }),

        exportComplaints: build.query<any, any>({
            query: ({ data, filterPrams }) => {
                return {
                    url: `${module_url}/bulk_downloads/complaints`,
                    useV3: true,
                    params: {
                        category: filterPrams?.category
                            ? TAB_TO_CATEGORY_LABEL[filterPrams?.category as keyof typeof TAB_TO_CATEGORY_LABEL]
                            : "all",
                        complaint_subtype_id: data.complaints_subtype_id?.length
                            ? JSON.stringify(data.complaints_subtype_id)
                            : "",
                        complaint_type_id: data.complaint_type_id?.length ? JSON.stringify(data.complaint_type_id) : "",
                        zone: data.zone?.length ? JSON.stringify(data.zone) : "",
                        country_id: data.country?.length ? JSON.stringify(data.country) : "",
                        status: data.status?.length ? JSON.stringify(data.status) : "",
                        state_id: data.state_id?.length ? JSON.stringify(data.state_id) : "",
                        city_id: data.city_id?.length ? JSON.stringify(data.city_id) : "",
                        oem_id: data.oem_id?.length ? JSON.stringify(data.oem_id) : "",
                        account_id: data.account_id?.length ? JSON.stringify(data.account_id) : "",
                        search_key: data.search_key ?? "",
                        raised_by_email: data.raised_by_email ?? "",
                        priority: data.priority?.length ? JSON.stringify(data.priority) : "",
                        statiq_engineer_ids: data.statiq_engineer_ids?.length
                            ? JSON.stringify(data.statiq_engineer_ids)
                            : "",
                        asp_company_names: data.assigned_asp_name?.length ? JSON.stringify(data.assigned_asp_name) : "",
                        asp_engineer_ids: data.asp_engineer_ids?.length ? JSON.stringify(data.asp_engineer_ids) : "",
                        service_complaint_type_id: data.service_complaint_type_id?.length
                            ? JSON.stringify(data.service_complaint_type_id)
                            : "",
                        service_complaint_subtype_id: data.service_complaint_subtype_id?.length
                            ? JSON.stringify(data.service_complaint_subtype_id)
                            : "",
                        updated_complaint_type_id: data.update_complaint_type?.length
                            ? JSON.stringify(data.update_complaint_type)
                            : "",
                        updated_complaint_subtype_id: data.update_complaint_subtype?.length
                            ? JSON.stringify(data.update_complaint_subtype)
                            : "",
                        parent_account_id: data.parent_account_id?.length ? JSON.stringify(data.parent_account_id) : "",
                        department: data.department?.length ? JSON.stringify(data.department) : "",
                        resolution_remark_id: data.resolution_remark_id?.length
                            ? JSON.stringify(data.resolution_remark_id?.[0])
                            : "",
                        charger_type_filter: data.charger_type?.length ? data.charger_type?.[0] : "",
                        sku_filter: data.charger_sku?.length ? data.charger_sku?.[0] : "",
                        panel_type: filterPrams.panel_type,
                        part_replacement: data.part_replacement ?? "",
                        site_visit: data.site_visit ?? "",
                        on_hold: data.on_hold?.length ? data.on_hold?.[0] : "",
                        acceptance_status: data.acceptance_status?.length ? data.acceptance_status.join(",") : "",
                        all_complaints: data.all_complaints ?? 1,
                        created_at_start: data.created_at_start ?? "",
                        created_at_end: data?.created_at_end ?? "",
                        user_email: filterPrams?.user_email,
                        ...(filterPrams.is_executive && { is_executive: filterPrams.is_executive }),
                        ...(filterPrams.engineer_id && { engineer_id: filterPrams.engineer_id }),
                        source_system: data?.source_system ?? "",
                        doa: data.doa?.length ? data.doa.join(",") : "",
                        age: data?.age ?? "[]",
                        age_op: data?.age_op ?? "",
                    },
                    responseHandler: async (response: any) => {
                        const contentType = response.headers.get("content-type")

                        if (contentType?.includes("application/json")) {
                            return await response.json()
                        }

                        return await response.blob()
                    },
                }
            },
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    if (res.data?.msg) {
                        enqueueSnackbar(res.data.msg, { variant: "success" })
                        return
                    }
                } catch (error: any) {}
            },
            keepUnusedDataFor: 0,
        }),

        getPlannedVisitDate: build.query<any, { complaint_id: number | string }>({
            query: params => ({
                url: `${module_url}/complaint_planned_visit?complaint_id=${params.complaint_id}`,
                useV3: true,
            }),
            providesTags: ["Complaints"],
        }),
        getSiteVisits: build.query<any, { complaint_id: number }>({
            query: ({ complaint_id }) => ({
                url: `${module_url}/visits?complaint_id=${complaint_id}`,
                useV3: true,
            }),
            providesTags: ["SiteVisit"],
        }),
        addVisitDate: build.mutation<any, { complaint_id: number | string; planned_visit_date: string }>({
            query: ({ complaint_id, planned_visit_date }) => ({
                url: `${module_url}/complaint_planned_visit`,
                useV3: true,
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: { complaint_id, planned_visit_date },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    enqueueSnackbar("Planned Visit Date Added", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar("Failed to add Planned Visit Date", { variant: "error" })
                }
            },
            invalidatesTags: ["SiteVisit", "Complaints"],
        }),
        assignDepartment: build.mutation<any, { complaint_id: number | string; department: string }>({
            query: ({ complaint_id, department }) => ({
                url: `${module_url}/department`,
                useV3: true,
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: { complaint_id, department },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    enqueueSnackbar("Department Updated Successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar("Failed to update Department", { variant: "error" })
                }
            },
            invalidatesTags: ["Complaints"],
        }),
        downloadPartReplacement: build.query<any, { data: any; filterPrams: any }>({
            query: ({ data, filterPrams }) => ({
                url: `${module_url}/download_part_replacement`,
                useV3: true,
                params: {
                    category: filterPrams?.category
                        ? TAB_TO_CATEGORY_LABEL[filterPrams?.category as keyof typeof TAB_TO_CATEGORY_LABEL]
                        : "all",
                    complaint_subtype_id: data.complaints_subtype_id?.length
                        ? JSON.stringify(data.complaints_subtype_id)
                        : "",
                    complaint_type_id: data.complaint_type_id?.length ? JSON.stringify(data.complaint_type_id) : "",
                    zone: data.zone?.length ? JSON.stringify(data.zone) : "",
                    country_id: data.country?.length ? JSON.stringify(data.country) : "",
                    status: data.status?.length ? JSON.stringify(data.status) : "",
                    state_id: data.state_id?.length ? JSON.stringify(data.state_id) : "",
                    city_id: data.city_id?.length ? JSON.stringify(data.city_id) : "",
                    oem_id: data.oem_id?.length ? JSON.stringify(data.oem_id) : "",
                    account_id: data.account_id?.length ? JSON.stringify(data.account_id) : "",
                    search_key: data.search_key ?? "",
                    raised_by_email: data.raised_by_email ?? "",
                    priority: data.priority?.length ? JSON.stringify(data.priority) : "",
                    statiq_engineer_ids: data.statiq_engineer_ids?.length
                        ? JSON.stringify(data.statiq_engineer_ids)
                        : "",
                    asp_company_names: data.assigned_asp_name?.length ? JSON.stringify(data.assigned_asp_name) : "",
                    asp_engineer_ids: data.asp_engineer_ids?.length ? JSON.stringify(data.asp_engineer_ids) : "",
                    service_complaint_type_id: data.service_complaint_type_id?.length
                        ? JSON.stringify(data.service_complaint_type_id)
                        : "",
                    service_complaint_subtype_id: data.service_complaint_subtype_id?.length
                        ? JSON.stringify(data.service_complaint_subtype_id)
                        : "",
                    updated_complaint_type_id: data.update_complaint_type?.length
                        ? JSON.stringify(data.update_complaint_type)
                        : "",
                    updated_complaint_subtype_id: data.update_complaint_subtype?.length
                        ? JSON.stringify(data.update_complaint_subtype)
                        : "",
                    parent_account_id: data.parent_account_id?.length ? JSON.stringify(data.parent_account_id) : "",
                    department: data.department?.length ? JSON.stringify(data.department) : "",
                    resolution_remark_id: data.resolution_remark_id?.length
                        ? JSON.stringify(data.resolution_remark_id?.[0])
                        : "",
                    charger_type_filter: data.charger_type?.length ? data.charger_type?.[0] : "",
                    sku_filter: data.charger_sku?.length ? data.charger_sku?.[0] : "",
                    panel_type: filterPrams.panel_type,
                    part_replacement: data.part_replacement ?? "",
                    site_visit: data.site_visit ?? "",
                    on_hold: data.on_hold?.length ? data.on_hold?.[0] : "",
                    acceptance_status: data.acceptance_status?.length ? data.acceptance_status.join(",") : "",
                    all_complaints: data.all_complaints ?? 1,
                    created_at_start: data.created_at_start ?? "",
                    created_at_end: data?.created_at_end ?? "",
                    user_email: filterPrams?.user_email,
                    ...(filterPrams.is_executive && { is_executive: filterPrams.is_executive }),
                    ...(filterPrams.engineer_id && { engineer_id: filterPrams.engineer_id }),
                    source_system: data?.source_system ?? "",
                    age: data?.age ?? "[]",
                    age_op: data?.age_op ?? "",
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    window.open(data.urls.download, "_blank")
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
        }),
        addPartReplacement: build.mutation<any, FormData>({
            query: body => ({
                url: `${module_url}/part_replacement`,
                useV3: true,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    enqueueSnackbar(data?.Success || "Updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["PartReplacement"],
        }),
        editPartReplacement: build.mutation<any, any>({
            query: body => ({
                url: `${module_url}/part_replacement`,
                useV3: true,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    enqueueSnackbar(data?.Success || "Updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["ComplaintsAction", "PartReplacement"],
        }),
        deletePartReplacement: build.mutation<any, any>({
            query: body => ({
                url: `${module_url}/part_replacement`,
                useV3: true,
                method: "DELETE",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.Success || "Updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["PartReplacement"],
        }),
        editComplaintsTicket: build.mutation<any, FormData>({
            query: body => ({
                url: `${module_url}`,
                useV3: true,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.Success || "Complaint updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.data?.Error ||
                            error?.error?.data?.error ||
                            error?.error?.data?.message ||
                            error?.message ||
                            "Something went wrong!",
                        { variant: "error" }
                    )
                }
            },
            invalidatesTags: ["ComplaintsAction"],
        }),
        addComplaintVisit: build.mutation<any, any>({
            query: body => ({
                url: `${module_url}/visits`,
                useV3: true,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    enqueueSnackbar(data?.Success || "Visit Added Successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
        }),
        getVisitCost: build.query<any, any>({
            query: params => ({
                url: `${module_url}/visits/costs`,
                useV3: true,
                params: params,
            }),
            providesTags: ["VisitCost"],
        }),
        // Checklist master — CHECK questions & MEDIA slots for a complaint. Pass the
        // complaint's classification so future type/subtype-specific items are included
        // (all items are global today, so results are the same regardless).
        getSiteChecklistQuestions: build.query<
            ChecklistItem[],
            { complaint_type_code?: string; complaint_subtype_code?: string } | void
        >({
            query: params => ({
                url: `${module_url}/site_checklist_questions`,
                useV3: true,
                params: params || undefined,
            }),
        }),
        // Expense-category dropdown — each category maps to a product (sku_code) and,
        // for the resolved account, its rate-card cost. account_id wins over
        // engineering_company_id; rate_card_amount is null when no account resolves.
        getSiteVisitExpenseCategories: build.query<
            ExpenseCategory[],
            { account_id?: number | string; engineering_company_id?: number | string } | void
        >({
            query: params => ({
                url: `${module_url}/site_visit_expense_categories`,
                useV3: true,
                params: params || undefined,
            }),
        }),
        addVisitCost: build.mutation<any, any>({
            query: body => ({
                url: `${module_url}/visits/costs`,
                useV3: true,
                method: "POST",
                body: body,
            }),
            invalidatesTags: ["VisitCost", "SiteVisit"],
        }),
        updateVisitSite: build.mutation<any, any>({
            query: body => ({
                url: `${module_url}/visits`,
                useV3: true,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    enqueueSnackbar(data?.Success || "Updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["SiteVisit"],
        }),
        updateVisitCost: build.mutation<any, any>({
            query: body => ({
                url: `${module_url}/visits/costs`,
                useV3: true,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    enqueueSnackbar(data?.Success || "Updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["VisitCost", "SiteVisit"],
        }),
        deleteSiteVisit: build.mutation<any, any>({
            query: body => ({
                url: `${module_url}/visits`,
                useV3: true,
                method: "DELETE",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    enqueueSnackbar(data?.Success || "Updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["SiteVisit"],
        }),
        editComplaintButtonStatus: build.mutation<any, FormData>({
            query: body => ({
                url: `${module_url}`,
                useV3: true,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.Success || "Status updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.data?.Error ||
                            error?.error?.data?.error ||
                            error?.error?.data?.message ||
                            error?.message ||
                            "Something went wrong!",
                        { variant: "error" }
                    )
                }
            },
            invalidatesTags: ["Complaints", "ComplaintsAction"],
        }),
        // Deferral reasons for the "Mark as Deferred" dropdown.
        getDeferReasons: build.query<{ id: string; title: string }[], void>({
            query: () => ({
                url: `${module_url}/defer_reasons`,
                useV3: true,
            }),
            transformResponse: (response: any) =>
                (response ?? [])
                    .filter((item: any) => item?.is_active)
                    .sort((a: any, b: any) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
                    .map((item: any) => ({ id: item?.code ?? item?.id, title: item?.label })),
        }),
        // On-hold reasons for the "Put On-hold" dropdown.
        getHoldReasons: build.query<{ id: string; title: string }[], void>({
            query: () => ({
                url: `${module_url}/hold_reasons`,
                useV3: true,
            }),
            transformResponse: (response: any) =>
                (response ?? [])
                    .filter((item: any) => item?.is_active)
                    .sort((a: any, b: any) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
                    .map((item: any) => ({ id: item?.code ?? item?.id, title: item?.label })),
        }),
        // Put a complaint on hold (POST /v3/complaint/hold, JSON). On-hold is not a status change.
        putComplaintOnHold: build.mutation<
            any,
            {
                complaint_id: number
                reason_code: string
                remarks?: string
                tentative_resume_at?: string | null
                user_id?: number
                user_name?: string
            }
        >({
            query: body => ({
                url: `${module_url}/hold`,
                useV3: true,
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body,
            }),
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.Success || data?.message || "Complaint put on hold", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["Complaints", "ComplaintsAction"],
        }),
        // Resume a complaint from on-hold (POST /v3/complaint/resume, JSON).
        resumeComplaint: build.mutation<
            any,
            { complaint_id: number; remarks?: string; user_id?: number; user_name?: string }
        >({
            query: body => ({
                url: `${module_url}/resume`,
                useV3: true,
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body,
            }),
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.Success || data?.message || "Complaint resumed", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["Complaints", "ComplaintsAction"],
        }),
        // JSON status change (PUT /v3/complaint) — e.g. moving a complaint to DEFERRED with a
        // defer_reason_code and optional comment.
        changeComplaintStatus: build.mutation<
            any,
            { complaint_id: number; status: string; defer_reason_code?: string; comment?: string }
        >({
            query: body => ({
                url: `${module_url}`,
                useV3: true,
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.Success || "Status updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["Complaints", "ComplaintsAction"],
        }),
        // Inline complaint update (PUT /v3/complaint, JSON). Sends the closing comment, staged
        // attachments, and nested site_visits[]/part_replacements[] in one Sherlock transaction
        // (all-or-nothing). Field names inside the arrays mirror POST /visits & POST /part_replacement.
        resolveComplaint: build.mutation<
            any,
            {
                complaint_id: number
                user_id: number
                status: string
                service_complaint_type_id?: string
                service_complaint_subtype_id?: string
                resolution_remark_id?: string | number
                comment?: string
                staged_attachments?: { staged_id: string }[]
                site_visits?: any[]
                part_replacements?: any[]
                // Checklist edits to already-saved visits, applied in the same PUT.
                site_visit_checklist_edits?: any[]
            }
        >({
            query: body => ({
                url: `${module_url}`,
                useV3: true,
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.Success || "Complaint Updated", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["Complaints", "ComplaintsAction", "SiteVisit", "PartReplacement", "ComplaintComments"],
        }),
        acceptAssignment: build.mutation<
            any,
            {
                complaint_id: number | string
                user_id?: number
                user_name?: string
                engineer_type: "ENGINEER" | "ASP_ENGINEER"
                planned_date: string
            }
        >({
            query: ({ complaint_id, user_id, user_name, engineer_type, planned_date }) => ({
                url: `${module_url}`,
                useV3: true,
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: {
                    complaint_id,
                    user_id,
                    user_name,
                    accept_assignment: { engineer_type, planned_date },
                },
            }),
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.Success || "Assignment accepted", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["Complaints", "ComplaintsAction"],
        }),
        // Reject a pending assignment. Sent alone (may only carry an optional top-level
        // `department` to route the ticket elsewhere on rejection). Sherlock re-triggers
        // auto-assignment for an ENGINEER rejection.
        rejectAssignment: build.mutation<
            any,
            {
                complaint_id: number | string
                user_id?: number
                user_name?: string
                engineer_type: "ENGINEER" | "ASP_ENGINEER"
                comment: string
                department?: string
            }
        >({
            query: ({ complaint_id, user_id, user_name, engineer_type, comment, department }) => ({
                url: `${module_url}`,
                useV3: true,
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: {
                    complaint_id,
                    user_id,
                    user_name,
                    ...(department ? { department } : {}),
                    reject_assignment: { engineer_type, comment },
                },
            }),
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.Success || "Assignment rejected", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["Complaints", "ComplaintsAction"],
        }),
        getComplaintPriority: build.query<any, any>({
            query: (params: any) => ({
                url: `${module_url}/priority`,
                useV3: true,
                params: params,
            }),
        }),
        getComplaintAssignedDepartments: build.query<any, any>({
            query: (params: any) => ({
                url: `${module_url}/department`,
                useV3: true,
                params: params,
            }),
            transformResponse: (response: any) => {
                return response?.map((item: any) => ({
                    id: item?.id,
                    title: item?.label,
                }))
            },
        }),
        getComplaintComments: build.query<any, any>({
            query: (params: any) => ({
                url: `/comments/comment`,
                useV3: true,
                params: params,
            }),
            providesTags: ["ComplaintComments"],
        }),
        editComplaintComments: build.mutation<any, any>({
            query: body => ({
                url: `/comments/comment`,
                useV3: true,
                method: "PUT",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    enqueueSnackbar(data?.Success || "Updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["ComplaintComments"],
        }),
        addComplaintComments: build.mutation<any, any>({
            query: body => ({
                url: `/comments/comment`,
                useV3: true,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    enqueueSnackbar(data?.Success || "Updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(getErrorMsg(error), { variant: "error" })
                }
            },
            invalidatesTags: ["ComplaintComments"],
        }),
        getComplaintPresignedUrl: build.mutation({
            query: body => ({
                url: `${module_url}/attachments`,
                useV3: true,
                method: "POST",
                body,
            }),
        }),
        uploadFileToPresignedUrl: build.mutation({
            query: (arg: any) => {
                return {
                    url: arg.url,
                    method: "PUT",
                    body: arg.payload,
                }
            },
        }),
        getComplaintSourceSystem: build.query<any, any>({
            query: (params: any) => ({
                url: `complaint/source_systems`,
                params: params,
            }),
            transformResponse: (response: any) => {
                return response?.map((item: any) => ({
                    id: item?.id,
                    title: item?.label,
                }))
            },
        }),
        getAllCinsForComplaints: build.query<
            any,
            {
                search_cin?: string
                search_project?: string
                account_id?: number | string
                page?: number
                per_page?: number
            }
        >({
            query: params => ({
                url: `${module_url}/get_all_cins_for_complaints`,
                useV3: true,
                params: {
                    ...(params.search_cin && { cin: params.search_cin }),
                    ...(params.search_project && { search_key: params.search_project }),
                    ...(params.account_id && { account_id: params.account_id }),
                    page: params.page ?? 1,
                    per_page: params.per_page ?? 10,
                },
            }),
        }),
        addComplaint: build.mutation<
            { data: { complaint_id: number; status_code: string }; message: string },
            FormData
        >({
            query: body => ({
                url: `${module_url}`,
                useV3: true,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    enqueueSnackbar("Complaint raised successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.data?.Error ||
                            error?.error?.data?.error ||
                            error?.error?.data?.message ||
                            "Failed to raise complaint",
                        { variant: "error" }
                    )
                }
            },
            invalidatesTags: ["Complaints"],
        }),
    }),
})

export const {
    useLazyGetComplaintMetricsQuery,
    useLazyGetAllComplaintsQuery,
    useGetParentAccountsQuery,
    useGetEngineersDataQuery,
    useGetASPDataQuery,
    useLazyGetComplaintDetailQuery,
    useGetResolutionRemarksQuery,
    useLazyGetComplaintsTypeQuery,
    useGetComplaintsTypeQuery,
    useGetComplaintsSubTypeQuery,
    useLazyGetComplaintsSubTypeQuery,
    useGetAccountsQuery,
    useGetPartReplacementQuery,
    useGetResolvedResolutionRemarksQuery,
    useGetAspEngineersDataQuery,
    useLazyGetAspEngineersDataQuery,
    useLazyGetComplaintsButtonDetailQuery,
    useGetComplaintsButtonDetailQuery,
    useLazyExportComplaintsQuery,
    useGetPlannedVisitDateQuery,
    useAddVisitDateMutation,
    useAssignDepartmentMutation,
    useEditComplaintsTicketMutation,
    useAddPartReplacementMutation,
    useGetEngineerCompaniesQuery,
    useAddComplaintVisitMutation,
    useAddVisitCostMutation,
    useLazyGetSiteVisitsQuery,
    useDeletePartReplacementMutation,
    useEditPartReplacementMutation,
    useEditComplaintButtonStatusMutation,
    useGetDeferReasonsQuery,
    useGetHoldReasonsQuery,
    usePutComplaintOnHoldMutation,
    useResumeComplaintMutation,
    useAcceptAssignmentMutation,
    useRejectAssignmentMutation,
    useChangeComplaintStatusMutation,
    useResolveComplaintMutation,
    useGetSiteVisitsQuery,
    useUpdateVisitSiteMutation,
    useDeleteSiteVisitMutation,
    useUpdateVisitCostMutation,
    useGetVisitCostQuery,
    useGetSiteChecklistQuestionsQuery,
    useLazyGetSiteChecklistQuestionsQuery,
    useGetSiteVisitExpenseCategoriesQuery,
    useLazyGetSiteVisitExpenseCategoriesQuery,
    useLazyDownloadPartReplacementQuery,
    useGetComplaintPresignedUrlMutation,
    useLazyGetComplaintCommentsQuery,
    useEditComplaintCommentsMutation,
    useAddComplaintCommentsMutation,
    useGetComplaintPriorityQuery,
    useUploadFileToPresignedUrlMutation,
    useGetComplaintAssignedDepartmentsQuery,
    useLazyGetComplaintTabsDataQuery,
    useGetComplaintSourceSystemQuery,
    useGetAllCinsForComplaintsQuery,
    useLazyGetAllCinsForComplaintsQuery,
    useAddComplaintMutation,
} = complaintsService
